/**
 * LoansPage.tsx
 *
 * Full loans management page. Lists loans with search, status filter,
 * date range filter, pagination, create/edit modal, and delete confirmation.
 */
import { useState, useCallback, useEffect, useMemo } from 'react';
import { PatientLink } from '@/components/ui/PatientLink';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Download } from 'lucide-react';
import {
  listLoans,
  createLoan,
  updateLoan,
  deleteLoan,
  getLoanStats,
  type Loan,
  type LoanListParams,
  type CreateLoanPayload,
  type UpdateLoanPayload,
} from '@/services/loans.service';
import { listLoanTypes, type AdminLoanType } from '@/services/loanTypesAdmin.service';
import { FilterSidebar } from '@/components/layout/FilterSidebar';
import { ListPageLayout } from '@/components/layout/ListPageLayout';
import { PageHeader } from '@/components/layout/PageHeader';
import { DataTable } from '@/components/ui/DataTable';
import type { DataTableColumn, DataTableAction } from '@/components/ui/DataTable';
import { MetricCardGrid } from '@/components/ui/MetricCardGrid';
import { StatCard } from '@/components/ui/StatCard';
import { Modal } from '@/components/ui/Modal';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Badge } from '@/components/ui/Badge';
import { FormField } from '@/components/ui/FormField';
import { PatientSearchInput } from '@/components/medical/PatientSearchInput';
import { toast } from '@/store/toastStore';
import { usePageContext } from '@/hooks/usePageContext';
import type { PatientSummary } from '@/types/patient.types';
import type { BadgeVariant } from '@/types/common.types';
import type { FilterConfig, FilterOption, FilterValue } from '@/types/filters.types';

// ─── Filter configs ──────────────────────────────────────────────────────────

const FILTER_CONFIGS: FilterConfig[] = [
  { key: 'search', label: 'Search', type: 'search', allLabel: 'Search loans...' },
  { key: 'daterange', label: 'Date', type: 'daterange' },
  { key: 'status', label: 'Status', type: 'select', allLabel: 'All statuses' },
];

// ─── Constants ────────────────────────────────────────────────────────────────

const STATUS_OPTIONS = [
  { label: 'Active', value: 'active' },
  { label: 'Paid', value: 'paid' },
  { label: 'Defaulted', value: 'defaulted' },
  { label: 'Cancelled', value: 'cancelled' },
];

const INTERVAL_OPTIONS = [
  { label: 'Monthly', value: 'monthly' },
  { label: 'Weekly', value: 'weekly' },
  { label: 'Biweekly', value: 'biweekly' },
];

const LOAN_STATUS_OPTIONS = [
  { label: 'Active', value: 'active' },
  { label: 'Paid', value: 'paid' },
  { label: 'Defaulted', value: 'defaulted' },
  { label: 'Cancelled', value: 'cancelled' },
];

const DUE_DAY_OPTIONS = [
  { label: 'Not Set', value: '' },
  ...Array.from({ length: 28 }, (_, i) => ({
    label: String(i + 1),
    value: String(i + 1),
  })),
];

const CUSTOM_LOAN_TYPE = '__custom__';

const PAGE_SIZE = 25;

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatCurrency(value: number | null): string {
  if (value == null) return '--';
  return `$${value.toFixed(2)}`;
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) return '--';
  return new Date(dateStr).toLocaleDateString();
}

function getStatusVariant(status: string | null): BadgeVariant {
  switch (status) {
    case 'active':
      return 'info';
    case 'paid':
      return 'success';
    case 'defaulted':
      return 'error';
    case 'cancelled':
      return 'warning';
    default:
      return 'neutral';
  }
}

function patientName(loan: Loan): string {
  const parts = [loan.patientFirstName, loan.patientLastName].filter(Boolean);
  return parts.length > 0 ? parts.join(' ') : '--';
}

// ─── CSV Export ──────────────────────────────────────────────────────────────

function escapeCsvField(value: string): string {
  if (value.includes(',') || value.includes('"') || value.includes('\n')) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}

function exportLoansCsv(loans: Loan[]) {
  const headers = [
    'Patient',
    'Loan Type',
    'Amount',
    'Paid',
    'Balance',
    'Interval',
    'Months',
    'Interval Amount',
    'Late Fee',
    'Interest Rate',
    'Interest Type',
    'Status',
    'Created',
    'End Date',
    'Paid Date',
  ];

  const rows = loans.map((l) => [
    patientName(l),
    l.loanType ?? '',
    String(l.loanAmount ?? ''),
    String(l.paidAmount ?? ''),
    String(l.balance ?? ''),
    l.loanInterval ?? '',
    l.loanMonths != null ? String(l.loanMonths) : '',
    l.intervalAmount != null ? String(l.intervalAmount) : '',
    l.lateFee != null ? String(l.lateFee) : '',
    l.interestRate != null ? String(l.interestRate) : '',
    l.interestType ?? '',
    l.loanStatus ?? '',
    l.createDate ?? '',
    l.endDate ?? '',
    l.paidDate ?? '',
  ]);

  const csv = [headers, ...rows].map((row) => row.map(escapeCsvField).join(',')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `loans-export-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

/**
 * Calculate the next due date based on loan interval and create date.
 * Returns null if information is insufficient or the loan is not active.
 */
function computeNextDueDate(loan: Loan): Date | null {
  if (!loan.createDate || !loan.loanInterval || loan.loanStatus !== 'active') {
    return null;
  }

  const start = new Date(loan.createDate);
  const now = new Date();
  let intervalDays: number;

  switch (loan.loanInterval) {
    case 'weekly':
      intervalDays = 7;
      break;
    case 'biweekly':
      intervalDays = 14;
      break;
    case 'monthly':
    default:
      // For monthly, step month-by-month from createDate until we find a future date
      {
        const next = new Date(start);
        while (next <= now) {
          next.setMonth(next.getMonth() + 1);
        }
        return next;
      }
  }

  // For weekly/biweekly: advance by interval until we find a future date
  const next = new Date(start);
  while (next <= now) {
    next.setDate(next.getDate() + intervalDays);
  }
  return next;
}

/**
 * Determine if a loan is overdue: active, and the next due date is in the past
 * (meaning the *previous* due date has passed without the loan being paid off).
 */
function isOverdue(loan: Loan): boolean {
  if (loan.loanStatus !== 'active' || !loan.loanInterval || !loan.createDate) {
    return false;
  }
  if (loan.balance <= 0) return false;

  const start = new Date(loan.createDate);
  const now = new Date();
  // If the loan hasn't even had its first interval pass, it's not overdue
  if (start >= now) return false;

  const nextDue = computeNextDueDate(loan);
  if (!nextDue) return false;

  // Overdue if today is past the most recent due date (next due - interval)
  let intervalMs: number;
  switch (loan.loanInterval) {
    case 'weekly':
      intervalMs = 7 * 24 * 60 * 60 * 1000;
      break;
    case 'biweekly':
      intervalMs = 14 * 24 * 60 * 60 * 1000;
      break;
    case 'monthly':
    default:
      // Approximate: check if next due is more than a day away from today
      // If the next due date is in the future but the previous one has passed,
      // the loan is overdue if there's a remaining balance
      {
        const prevDue = new Date(nextDue);
        prevDue.setMonth(prevDue.getMonth() - 1);
        return prevDue < now && loan.balance > 0;
      }
  }

  const prevDue = new Date(nextDue.getTime() - intervalMs);
  return prevDue < now && loan.balance > 0;
}

// ─── Empty form state ─────────────────────────────────────────────────────────

interface LoanFormState {
  patientId: string;
  loanType: string;
  loanTypeSelect: string; // dropdown value: loanType title, or CUSTOM_LOAN_TYPE
  loanAmount: string;
  loanInterval: string;
  loanMonths: string;
  intervalAmount: string;
  dueDay: string;
  lateFee: string;
  interestRate: string;
  interestType: string;
  loanStatus: string;
}

const EMPTY_FORM: LoanFormState = {
  patientId: '',
  loanType: '',
  loanTypeSelect: '',
  loanAmount: '',
  loanInterval: 'monthly',
  loanMonths: '',
  intervalAmount: '',
  dueDay: '',
  lateFee: '',
  interestRate: '',
  interestType: '',
  loanStatus: 'active',
};

function loanToForm(loan: Loan, loanTypes?: AdminLoanType[]): LoanFormState {
  // Determine if the current loanType matches an admin-defined type
  const matchedType = loanTypes?.find((lt) => lt.title === loan.loanType);
  return {
    patientId: loan.patientId != null ? String(loan.patientId) : '',
    loanType: loan.loanType ?? '',
    loanTypeSelect: matchedType ? matchedType.title : loan.loanType ? CUSTOM_LOAN_TYPE : '',
    loanAmount: String(loan.loanAmount),
    loanInterval: loan.loanInterval ?? 'monthly',
    loanMonths: loan.loanMonths != null ? String(loan.loanMonths) : '',
    intervalAmount: loan.intervalAmount != null ? String(loan.intervalAmount) : '',
    dueDay: '',
    lateFee: loan.lateFee != null ? String(loan.lateFee) : '',
    interestRate: loan.interestRate != null ? String(loan.interestRate) : '',
    interestType: loan.interestType ?? '',
    loanStatus: loan.loanStatus ?? 'active',
  };
}

function formToCreatePayload(form: LoanFormState): CreateLoanPayload {
  return {
    patientId: form.patientId ? Number(form.patientId) : null,
    loanType: form.loanType || null,
    loanAmount: Number(form.loanAmount),
    loanInterval: form.loanInterval || null,
    loanMonths: form.loanMonths ? Number(form.loanMonths) : null,
    intervalAmount: form.dueDay ? Number(form.dueDay) : form.intervalAmount ? Number(form.intervalAmount) : null,
    lateFee: form.lateFee ? Number(form.lateFee) : null,
    interestRate: form.interestRate ? Number(form.interestRate) : null,
    interestType: form.interestType || null,
    loanStatus: form.loanStatus || 'active',
  };
}

function formToUpdatePayload(form: LoanFormState): UpdateLoanPayload {
  return formToCreatePayload(form);
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function LoansPage() {
  const queryClient = useQueryClient();
  const updateAIContext = usePageContext('Loans');

  // ── Filter / pagination state ───────────────────────────────────────────
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [page, setPage] = useState(1);

  // ── Modal state ─────────────────────────────────────────────────────────
  const [modalOpen, setModalOpen] = useState(false);
  const [editingLoan, setEditingLoan] = useState<Loan | null>(null);
  const [form, setForm] = useState<LoanFormState>(EMPTY_FORM);
  const [selectedPatient, setSelectedPatient] = useState<PatientSummary | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Loan | null>(null);
  const [deleteOpen, setDeleteOpen] = useState(false);

  // ── Query ───────────────────────────────────────────────────────────────
  const params: LoanListParams = {
    page,
    pageSize: PAGE_SIZE,
    search: search.trim() || undefined,
    status: statusFilter || undefined,
    dateFrom: dateFrom || undefined,
    dateTo: dateTo || undefined,
  };

  const { data, isLoading } = useQuery({
    queryKey: ['loans', params],
    queryFn: () => listLoans(params),
    placeholderData: (prev) => prev,
  });

  const { data: loanStats } = useQuery({
    queryKey: ['loans-stats'],
    queryFn: () => getLoanStats(),
    staleTime: 60_000,
  });

  const loans = data?.data ?? [];
  const total = data?.meta?.total ?? loanStats?.total ?? 0;
  const stats = {
    activeCount:  loanStats?.active       ?? 0,
    totalBalance: loanStats?.totalBalance ?? 0,
    pastDueCount: loanStats?.pastDue      ?? 0,
  };

  // ── AI page context ────────────────────────────────────────────────────────
  useEffect(() => {
    updateAIContext({
      filters: `Search: ${search || 'none'}, Status: ${statusFilter || 'all'}, Date: ${dateFrom || 'any'} to ${dateTo || 'any'}`,
      dataSummary: `${total} loans`,
    });
  }, [search, statusFilter, dateFrom, dateTo, total, updateAIContext]);

  // ── Loan types query ──────────────────────────────────────────────────
  // Admin > Payments > Loan Types (every type; the dropdown offers the live ones).
  const { data: loanTypes = [] } = useQuery({
    queryKey: ['loan-types'],
    queryFn: listLoanTypes,
    staleTime: 5 * 60 * 1000,
  });

  const loanTypeOptions = [
    { label: 'Select loan type...', value: '' },
    ...loanTypes.filter((lt) => lt.active).map((lt) => ({ label: lt.title, value: lt.title })),
    { label: 'Custom', value: CUSTOM_LOAN_TYPE },
  ];

  // ── Sidebar filter memos ──────────────────────────────────────────────
  const sidebarOptions = useMemo<Record<string, FilterOption[]>>(
    () => ({
      status: STATUS_OPTIONS.map((item) => ({ value: item.value, label: item.label })),
    }),
    [],
  );

  const sidebarValues = useMemo<Record<string, FilterValue>>(
    () => ({
      search: search ? [search] : [],
      daterange: {
        from: dateFrom,
        to: dateTo,
      },
      status: statusFilter ? [statusFilter] : [],
    }),
    [search, dateFrom, dateTo, statusFilter],
  );

  const handleSidebarChange = useCallback((filterKey: string, value: FilterValue) => {
    switch (filterKey) {
      case 'search':
        if (Array.isArray(value)) {
          setSearch(value[0] ?? '');
          setPage(1);
        }
        break;
      case 'daterange':
        if (Array.isArray(value) || !('from' in value)) return;
        setDateFrom(value.from || '');
        setDateTo(value.to || '');
        setPage(1);
        break;
      case 'status':
        if (Array.isArray(value)) {
          setStatusFilter(value[0] ?? '');
          setPage(1);
        }
        break;
      default:
        break;
    }
  }, []);

  const handleSidebarClear = useCallback((filterKey: string) => {
    switch (filterKey) {
      case 'search':
        setSearch('');
        setPage(1);
        break;
      case 'daterange':
        setDateFrom('');
        setDateTo('');
        setPage(1);
        break;
      case 'status':
        setStatusFilter('');
        setPage(1);
        break;
      default:
        break;
    }
  }, []);

  // ── Mutations ───────────────────────────────────────────────────────────
  const createMutation = useMutation({
    mutationFn: (payload: CreateLoanPayload) => createLoan(payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['loans'] });
      closeModal();
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: UpdateLoanPayload }) =>
      updateLoan(id, payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['loans'] });
      closeModal();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => deleteLoan(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['loans'] });
      setDeleteOpen(false);
      setDeleteTarget(null);
    },
  });

  // ── Handlers ────────────────────────────────────────────────────────────
  function openCreate() {
    setEditingLoan(null);
    setForm(EMPTY_FORM);
    setSelectedPatient(null);
    setModalOpen(true);
  }

  function openEdit(loan: Loan) {
    setEditingLoan(loan);
    setForm(loanToForm(loan, loanTypes));
    // Reconstruct a PatientSummary stub for the search input display
    if (loan.patientId != null) {
      setSelectedPatient({
        patientId: loan.patientId,
        accountId: 0,
        firstName: loan.patientFirstName ?? '',
        lastName: loan.patientLastName ?? '',
        displayName: patientName(loan),
        displayId: null,
        dateOfBirth: null,
        phone: null,
        homePhone: null,
        cellPhone: null,
      } as PatientSummary);
    } else {
      setSelectedPatient(null);
    }
    setModalOpen(true);
  }

  function closeModal() {
    setModalOpen(false);
    setEditingLoan(null);
    setSelectedPatient(null);
    setForm(EMPTY_FORM);
  }

  function openDelete(loan: Loan) {
    setDeleteTarget(loan);
    setDeleteOpen(true);
  }

  const handleSubmit = useCallback(() => {
    if (editingLoan) {
      updateMutation.mutate({
        id: editingLoan.loanId,
        payload: formToUpdatePayload(form),
      });
    } else {
      createMutation.mutate(formToCreatePayload(form));
    }
  }, [editingLoan, form, createMutation, updateMutation]);

  function updateField(field: keyof LoanFormState, value: string) {
    setForm((prev) => ({ ...prev, [field]: value }));
  }

  function handlePatientSelect(patient: PatientSummary | null) {
    setSelectedPatient(patient);
    updateField('patientId', patient ? String(patient.patientId) : '');
  }

  function handleLoanTypeChange(value: string) {
    if (value === CUSTOM_LOAN_TYPE) {
      setForm((prev) => ({ ...prev, loanTypeSelect: CUSTOM_LOAN_TYPE, loanType: '' }));
    } else if (value) {
      // Apply template values from the matching loan type
      const template = loanTypes.find((lt) => lt.title === value);
      if (template) {
        setForm((prev) => ({
          ...prev,
          loanTypeSelect: value,
          loanType: template.title,
          // A type has no amount (legacy saved 0); its Due Day is the loan's.
          loanInterval: template.loanInterval || prev.loanInterval,
          loanMonths: template.loanMonths ? String(template.loanMonths) : prev.loanMonths,
          dueDay: template.dueDay >= 1 && template.dueDay <= 28 ? String(template.dueDay) : prev.dueDay,
          lateFee: String(template.lateFee),
          interestRate: String(template.interestRate),
          interestType: template.interestType || prev.interestType,
        }));
      }
    } else {
      setForm((prev) => ({ ...prev, loanTypeSelect: '', loanType: '' }));
    }
  }

  // ── Table columns ───────────────────────────────────────────────────────
  const columns: DataTableColumn<Loan>[] = [
    {
      key: 'patientFirstName',
      header: 'Patient',
      render: (row) =>
        row.patientId != null ? (
          <PatientLink
            patientId={row.patientId}
            patientName={patientName(row)}
            className="text-blue-600 hover:underline"
          >
            {patientName(row)}
          </PatientLink>
        ) : (
          patientName(row)
        ),
      sortable: true,
    },
    {
      key: 'loanType',
      header: 'Loan Type',
      render: (row) => row.loanType ?? '--',
      sortable: true,
    },
    {
      key: 'loanAmount',
      header: 'Amount',
      align: 'right',
      render: (row) => formatCurrency(row.loanAmount),
      sortable: true,
    },
    {
      key: 'paidAmount',
      header: 'Paid',
      align: 'right',
      render: (row) => formatCurrency(row.paidAmount),
    },
    {
      key: 'balance',
      header: 'Balance',
      align: 'right',
      render: (row) => formatCurrency(row.balance),
    },
    {
      key: 'loanInterval',
      header: 'Interval',
      render: (row) => row.loanInterval ?? '--',
    },
    {
      key: 'loanMonths',
      header: 'Months',
      align: 'right',
      render: (row) => (row.loanMonths != null ? String(row.loanMonths) : '--'),
    },
    {
      key: 'loanStatus',
      header: 'Status',
      render: (row) => {
        const overdue = isOverdue(row);
        return (
          <span className="inline-flex items-center gap-1.5">
            <Badge variant={overdue ? 'error' : getStatusVariant(row.loanStatus)}>
              {overdue ? 'Overdue' : (row.loanStatus ?? 'unknown')}
            </Badge>
          </span>
        );
      },
    },
    {
      key: 'createDate',
      header: 'Created',
      render: (row) => formatDate(row.createDate),
      sortable: true,
    },
    {
      key: 'endDate',
      header: 'Next Due',
      render: (row) => {
        const nextDue = computeNextDueDate(row);
        if (!nextDue) return '--';
        const overdue = isOverdue(row);
        const formatted = nextDue.toLocaleDateString();
        return overdue ? (
          <span className="text-red-600 font-medium">{formatted}</span>
        ) : (
          formatted
        );
      },
    },
  ];

  const rowActions: DataTableAction<Loan>[] = [
    { label: 'Edit', onClick: (row) => openEdit(row) },
    { label: 'Delete', onClick: (row) => openDelete(row), variant: 'danger' },
  ];

  const isSaving = createMutation.isPending || updateMutation.isPending;

  // ── Render ──────────────────────────────────────────────────────────────
  return (
    <>
      <ListPageLayout
        filterSidebar={(
          <FilterSidebar
            filters={FILTER_CONFIGS}
            values={sidebarValues}
            options={sidebarOptions}
            onChange={handleSidebarChange}
            onClear={handleSidebarClear}
            showAIAssistant={false}
          />
        )}
      >
        <div className="flex h-full min-h-0 flex-col">
          <div className="shrink-0 space-y-6 p-6">
            <PageHeader
              variant="compact"
              title="Loans"
              actions={
                <div className="flex items-center gap-2">
                  <Button
                    variant="secondary"
                    disabled={loans.length === 0}
                    onClick={() => {
                      exportLoansCsv(loans);
                      toast.success('Export', `Exported ${loans.length} loans to CSV.`);
                    }}
                  >
                    <Download className="h-4 w-4 mr-1.5" />
                    Export
                  </Button>
                  <Button onClick={openCreate}>+ New Loan</Button>
                </div>
              }
            />

            <MetricCardGrid columns={4}>
              <StatCard label="Total Loans" value={total} />
              <StatCard label="Active" value={stats.activeCount} />
              <StatCard label="Total Balance" value={formatCurrency(stats.totalBalance)} />
              <StatCard label="Past Due" value={stats.pastDueCount} />
            </MetricCardGrid>

          </div>

          <div className="min-h-0 flex-1 overflow-auto">
            <DataTable<Loan>
              data={loans}
              columns={columns}
              keyField="loanId"
              page={page}
              pageSize={PAGE_SIZE}
              total={total}
              onPageChange={setPage}
              rowActions={rowActions}
              loading={isLoading}
              emptyTitle="No loans found"
              emptyDescription="No loans match your current filters."
            />
          </div>
        </div>
      </ListPageLayout>

      {/* Create / Edit Modal */}
      <Modal
        isOpen={modalOpen}
        onClose={isSaving ? () => {} : closeModal}
        title={editingLoan ? 'Edit Loan' : 'New Loan'}
        size="lg"
        footer={
          <>
            <Button variant="secondary" onClick={closeModal} disabled={isSaving}>
              Cancel
            </Button>
            <Button onClick={handleSubmit} loading={isSaving}>
              {editingLoan ? 'Save Changes' : 'Create Loan'}
            </Button>
          </>
        }
      >
        <div className="grid grid-cols-2 gap-4">
          <FormField label="Patient" className="col-span-2">
            <PatientSearchInput
              onSelect={handlePatientSelect}
              selectedPatient={selectedPatient}
              placeholder="Search patients by name..."
              disabled={isSaving}
            />
          </FormField>

          <FormField label="Loan Type">
            <Select
              options={loanTypeOptions}
              value={form.loanTypeSelect}
              onChange={(val) => handleLoanTypeChange(val ?? '')}
              placeholder="Select loan type..."
            />
          </FormField>

          {form.loanTypeSelect === CUSTOM_LOAN_TYPE && (
            <FormField label="Custom Loan Type">
              <Input
                value={form.loanType}
                onChange={(e) => updateField('loanType', e.target.value)}
                placeholder="e.g. personal, medical"
              />
            </FormField>
          )}

          <FormField label="Loan Amount" required>
            <Input
              type="number"
              step="0.01"
              value={form.loanAmount}
              onChange={(e) => updateField('loanAmount', e.target.value)}
              placeholder="0.00"
            />
          </FormField>

          <FormField label="Interval">
            <Select
              options={INTERVAL_OPTIONS}
              value={form.loanInterval}
              onChange={(val) => updateField('loanInterval', val ?? 'monthly')}
            />
          </FormField>

          <FormField label="Months">
            <Input
              type="number"
              value={form.loanMonths}
              onChange={(e) => updateField('loanMonths', e.target.value)}
              placeholder="e.g. 12"
            />
          </FormField>

          <FormField label="Due Day (1-28)">
            <Select
              options={DUE_DAY_OPTIONS}
              value={form.dueDay}
              onChange={(val) => updateField('dueDay', val ?? '')}
              placeholder="Day of month"
            />
          </FormField>

          <FormField label="Interval Amount">
            <Input
              type="number"
              step="0.01"
              value={form.intervalAmount}
              onChange={(e) => updateField('intervalAmount', e.target.value)}
              placeholder="0.00"
            />
          </FormField>

          <FormField label="Late Fee">
            <Input
              type="number"
              step="0.01"
              value={form.lateFee}
              onChange={(e) => updateField('lateFee', e.target.value)}
              placeholder="0.00"
            />
          </FormField>

          <FormField label="Interest Rate">
            <Input
              type="number"
              step="0.01"
              value={form.interestRate}
              onChange={(e) => updateField('interestRate', e.target.value)}
              placeholder="e.g. 5.5"
            />
          </FormField>

          <FormField label="Interest Type">
            <Input
              value={form.interestType}
              onChange={(e) => updateField('interestType', e.target.value)}
              placeholder="e.g. simple, compound"
            />
          </FormField>

          <FormField label="Status">
            <Select
              options={LOAN_STATUS_OPTIONS}
              value={form.loanStatus}
              onChange={(val) => updateField('loanStatus', val ?? 'active')}
            />
          </FormField>
        </div>

        {(createMutation.isError || updateMutation.isError) && (
          <p className="mt-4 text-sm text-red-600">
            Failed to {editingLoan ? 'update' : 'create'} loan. Please try again.
          </p>
        )}
      </Modal>

      {/* Delete Confirmation */}
      <ConfirmDialog
        isOpen={deleteOpen}
        onClose={() => {
          setDeleteOpen(false);
          setDeleteTarget(null);
        }}
        onConfirm={async () => {
          if (deleteTarget) {
            deleteMutation.mutate(deleteTarget.loanId);
          }
        }}
        title="Delete Loan"
        message={
          deleteTarget
            ? `Are you sure you want to delete this loan for ${patientName(deleteTarget)}? This action cannot be undone.`
            : ''
        }
        confirmLabel="Delete"
        variant="danger"
      />
    </>
  );
}
